import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as tlsProfileSelectors from 'data/tlsProfile/tlsProfile.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as certStatusSelectors from 'data/certStatus/certStatus.selectors';
import * as tlsProfileActions from 'data/tlsProfile/tlsProfile.actions';
import * as certStatusActions from 'data/certStatus/certStatus.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { TlsProfile } from 'data/tlsProfile/tlsProfile.types';
import TLSProfileServerForm from 'routes/TlsProfile/components/TLSProfileServerForm';
import * as types from 'data/tlsProfile/tlsProfile.types';
import { CertStatus, CertStatusGridData } from 'data/certStatus/certStatus.types';
import { SelectionChangedEvent } from '@ag-grid-community/core';
import { AuditActionType } from 'shared/userAudit/audit-constants';
import { getCertStatusAuditDetailsToSubmit } from 'routes/TlsProfile/utils/certStatusDataToSubmit';
import { WithAuditDetails } from 'data/types';
import { adaptCertStatusGridData, getCertStatusPayload } from 'data/tlsProfile/tlsProfile.adapter';
import { tlsProfileTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  adaptFormData,
  adaptTlsProfileFormData,
  adaptTemplateOnFieldUpdate,
  adaptTemplateOnLoad
} from '../../utils/tlsProfileTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/tlsProfileTemplateDataToSubmit';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.TlsProfile
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  tlsProfileTemplateDef: adaptTemplateVisibilityMode(tlsProfileTemplate, 'edit'),
  deliveryFunctionId: state.global.selectedDF,
  selectedTlsProfile: tlsProfileSelectors.getSelectedTlsProfile(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedTlsProfile: tlsProfileSelectors.getSelectedTlsProfile(state)[0],
  allCertStatuses: certStatusSelectors.getAllCertStatus(state),
  tlsProfile: tlsProfileSelectors.getTlsProfile(state),
  availableAccessFunction: supportedFeaturesSelectors.getAvailableAccessFunction(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(state),
});

const mapDispatch = {
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToTlsProfileList: tlsProfileActions.goToTlsProfileList,
  updateTlsProfile: tlsProfileActions.updateTlsProfile,
  getTlsProfile: tlsProfileActions.getTlsProfile,
  getAllCertStatus: certStatusActions.getAllCertStatus,
  activateCertStatus: certStatusActions.activateCertStatus,
  deleteCertStatus: certStatusActions.deleteCertStatus,
  createCertStatus: certStatusActions.createCertStatus,
  createCertStatusAndGetAll: certStatusActions.createCertStatusAndGetAll
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const TlsProfileEdit = ({
  history,
  selectedTlsProfile,
  originalSelectedTlsProfile,
  deliveryFunctionId,
  userId,
  userName,
  isAuditEnabled,
  tlsProfileTemplateDef,
  tlsProfile,
  goToTlsProfileList,
  updateTlsProfile,
  getTlsProfile,
  allCertStatuses,
  enabledAccessFunctions,
  accessFunctionTemplates,
  getAllCertStatus,
  activateCertStatus,
  deleteCertStatus,
  createCertStatus,
  createCertStatusAndGetAll
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<TlsProfile>>({});
  const [template, setTemplate] = useState(tlsProfileTemplateDef);
  const [certStatusGridData, setCertStatusGridData] = useState<CertStatusGridData[]>([]);
  const [selectedCertStatus, setSelectedCertStatus] = useState<CertStatusGridData>();
  const { t } = useTranslation();

  const id = selectedTlsProfile?.id;

  useEffect(() => {
    if (id) {
      getTlsProfile(deliveryFunctionId, id);
      getAllCertStatus(deliveryFunctionId)
    }
  }, [deliveryFunctionId, id, getTlsProfile, getAllCertStatus]);

  useEffect(() => {
    if (tlsProfile != null && template != null) {
      let clonedTlsProfile = cloneDeep(tlsProfile);
      let clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      clonedTlsProfile = adaptTlsProfileFormData(clonedTlsProfile);
      clonedTemplate = adaptTemplateOnLoad(clonedTemplate, clonedTlsProfile);
      setTemplate(adaptTemplateOptions(clonedTemplate, enabledAccessFunctions, accessFunctionTemplates, tlsProfile));
      setFormData(clonedTlsProfile);
      setCertStatusGridData(adaptCertStatusGridData(allCertStatuses, tlsProfile));
    }
  }, [tlsProfile, allCertStatuses, accessFunctionTemplates, enabledAccessFunctions, id]);

  const getCreateDistributorActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const dialedTlsProfileData = formRef.current.getValues();

    if (dialedTlsProfileData.catype === 'XCCA') {
      dialedTlsProfileData.passphrase = 'NONE';
    }

    const payload = {
      data: dialedTlsProfileData,
      auditDetails: getAuditDetailsToSubmit(
        dialedTlsProfileData,
        originalSelectedTlsProfile,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateDistributorActionPayload();
    if (payload && tlsProfile != null) {
      updateTlsProfile(deliveryFunctionId, tlsProfile?.id, payload, history);
    }
  };

  const handleCancel = () => goToTlsProfileList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as TlsProfile);
    let clonedTemplate = cloneDeep(template);

    let newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptTemplateOnFieldUpdate(
      clonedTemplate,
      fieldName,
      value,
      newData,
      'edit',
    );

    newData = adaptFormData(newData, clonedTemplate, fieldName, value);

    setTemplate(adaptTemplateOptions(clonedTemplate, enabledAccessFunctions, accessFunctionTemplates, newData));
    setFormData(newData);
  };

  const handleRefreshCertStatuses = () => {
    getAllCertStatus(deliveryFunctionId);
  }

  const handleCertStatusSelectionChanged = (event: SelectionChangedEvent) => {
    setSelectedCertStatus(event.api.getSelectedRows()[0]);
  };

  const onCertActivate = () => {
    const found = allCertStatuses.find((cs) => cs.CERTID === selectedCertStatus?.id);
    if (!found) {
      return;
    }

    const certStatus = cloneDeep(found) as CertStatus;
    const payload = {
      data: certStatus,
      auditDetails: getCertStatusAuditDetailsToSubmit(
        certStatus,
        null,
        AuditActionType.CERTSTATUS_ACTIVATE,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };
    activateCertStatus(deliveryFunctionId, payload);
  }

  const getCreateCertStatusPayload = ():
    | WithAuditDetails<CertStatus>
    | undefined => {
    if (formRef.current === undefined || formRef.current === null) {
      return;
    }

    if (!formRef.current.isValid(true, true)) {
      return;
    }

    const formData = formRef.current.getValues();
    if (formData.catype !== 'XCCA') {
      return;
    }

    const activeCertExists = (allCertStatuses ?? []).some(
      (cs) =>
        cs.AFTYPE === formData.aftype &&
        cs.LITYPE === formData.litype &&
        (cs.STATUS === 'ACTIVATED' || cs.STATUS === 'DOWNLOADED')
    );

    if (activeCertExists) {
      return;
    }

    const data = getCertStatusPayload(formData);

    const payload = {
      data,
      auditDetails: getCertStatusAuditDetailsToSubmit(
        data,
        null,
        AuditActionType.CERTSTATUS_ADD,
        isAuditEnabled,
        userId,
        userName,
        'CREATE'
      )
    };

    return payload;
  };

  const onCertDelete = () => {
    const found = allCertStatuses.find((cs) => cs.CERTID === selectedCertStatus?.id);
    if (!found) {
      return;
    }
    const certStatus = cloneDeep(found) as CertStatus;
    const payload = {
      data: certStatus,
      auditDetails: getCertStatusAuditDetailsToSubmit(
        certStatus,
        null,
        AuditActionType.CERTSTATUS_DELETE,
        isAuditEnabled,
        userId,
        userName,
        'DELETE',
      ),
    };
    deleteCertStatus(deliveryFunctionId, certStatus.id, payload);
  }

  const onRequestCert = () => {
    const payload = getCreateCertStatusPayload();
    if (payload) {
      createCertStatusAndGetAll(deliveryFunctionId, payload);
    }
  }

  const renderForm = () => {
    if (formData && template) {
      return (
        <TLSProfileServerForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
          certGridData={certStatusGridData}
          handleRefreshCertStatuses={handleRefreshCertStatuses}
          handleCertStatusSelectionChanged={handleCertStatusSelectionChanged}
          onCertActivate={onCertActivate}
          onCertDelete={onCertDelete}
          onRequestCert={onRequestCert}
          selectedCertStatus={selectedCertStatus}
        />
      );
    }
  };
  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (tlsProfile == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="TlsProfileEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('tlsProfile.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(TlsProfileEdit));
